import React, { useState, useRef, useEffect } from 'react';
import { Send, User, AlertCircle, Headphones } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';


function cleanText(text) {
  if (!text) return '';
  return text
    .replace(/#{1,6}\s*/g, '')
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')
    .replace(/_{1,2}([^_]+)_{1,2}/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/~{2}([^~]+)~{2}/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^[-*]\s/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function SupportPage() {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([
    { sender: 'ai', text: lang === 'es' 
      ? 'Hola! Soy tu asistente en PulseMusic. Preguntame lo que necesites, estoy aqui para ayudarte.' 
      : 'Hey! I\'m your PulseMusic assistant. Ask me anything, I\'m here to help.' }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEnd = useRef(null);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const userMsg = (text || input).trim();
    if (!userMsg || sending) return;
    setInput('');
    setMessages(prev => [...prev, { sender: 'user', text: userMsg }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/api/support/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, context: 'support' })
      });
      const data = await res.json();
      setMessages(prev => [...prev, { 
        sender: 'ai', text: cleanText(data.response), escalated: data.needs_escalation 
      }]);
    } catch (e) {
      setMessages(prev => [...prev, { 
        sender: 'ai', text: lang === 'es' ? 'Ups, algo fallo. Intenta de nuevo o escribe a soporte directamente.' : 'Oops, something went wrong. Try again or contact support.'
      }]);
    }
    setSending(false);
  };

  const faqs = lang === 'es' ? [
    { q: 'Como contrato un artista?', emoji: '' },
    { q: 'Como funciona el reproductor?', emoji: '' },
    { q: 'Puedo crear playlists?', emoji: '' },
    { q: 'Como recargo mi monedero?', emoji: '' },
    { q: 'Como envio un apoyo?', emoji: '' },
  ] : [
    { q: 'How do I hire an artist?', emoji: '' },
    { q: 'How does the player work?', emoji: '' },
    { q: 'Can I create playlists?', emoji: '' },
    { q: 'How do I add funds?', emoji: '' },
    { q: 'How do I donate?', emoji: '' },
  ];

  return (
    <div data-testid="support-page" className="min-h-screen pt-14 pb-32">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="font-heading font-bold text-2xl mb-2 flex items-center gap-2">
          <Headphones size={22} className="text-pulse-primary" />
          {t('support.title')}
        </h1>
        <p className="text-xs text-pulse-muted mb-6">{lang === 'es' ? 'Tu asistente personal' : 'Your personal assistant'}</p>

        <div className="flex flex-wrap gap-2 mb-4">
          {faqs.map(faq => (
            <button key={faq.q} onClick={() => sendMessage(faq.q)}
              className="text-xs bg-pulse-surface border border-pulse-border px-3 py-2 rounded-xl hover:border-cyan-500/30 transition-colors duration-200"
            >{faq.q}</button>
          ))}
        </div>

        <div className="bg-pulse-surface border border-pulse-border rounded-2xl overflow-hidden">
          <div className="h-[55vh] overflow-y-auto p-4 space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-2.5 ${msg.sender === 'user' ? 'flex-row-reverse' : ''} animate-fade-in-up`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  msg.sender === 'user' ? 'bg-pulse-primary' : 'bg-pulse-elevated border border-pulse-border'
                }`}>
                  {msg.sender === 'user' ? <User size={12} className="text-black" /> : <Headphones size={12} className="text-pulse-primary" />}
                </div>
                <div className={`max-w-[80%] ${msg.sender === 'user' ? 'text-right' : ''}`}>
                  <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                    msg.sender === 'user'
                      ? 'bg-pulse-primary text-black rounded-tr-md'
                      : 'bg-pulse-elevated text-white/90 rounded-tl-md'
                  }`}>{msg.text}</div>
                  {msg.escalated && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-yellow-400">
                      <AlertCircle size={10} /> {lang === 'es' ? 'Pasamos tu caso al equipo de gestión' : 'Escalated to the operations team'}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full bg-pulse-elevated border border-pulse-border flex items-center justify-center">
                  <Headphones size={12} className="text-pulse-primary" />
                </div>
                <div className="bg-pulse-elevated rounded-2xl rounded-tl-md px-4 py-3 flex gap-1.5">
                  <span className="w-1.5 h-1.5 bg-pulse-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-pulse-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-pulse-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          <div className="border-t border-pulse-border p-3 flex gap-2">
            <input data-testid="support-input" type="text" value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder={lang === 'es' ? 'Escribe tu mensaje...' : 'Type your message...'}
              className="flex-1 bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
            />
            <button data-testid="support-send" onClick={() => sendMessage()} disabled={sending || !input.trim()}
              className="bg-pulse-primary text-black p-2.5 rounded-xl hover:bg-pulse-primary-hover transition-colors duration-200 disabled:opacity-50"
            ><Send size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
